/**
 * The one install surface. Both entry points — the user menu and the first-run nudge —
 * open this.
 *
 * Opening it is what acquires the service worker on Chromium (see `useInstallPrompt`), and
 * the time spent reading it is what covers the worker's activation. Closing it without
 * installing gives the worker back.
 *
 * On Safari it is instructions and nothing else, which is not a degraded path: iOS has no
 * install API at all, and iOS is the platform where installing actually matters, because
 * it is the precondition for notifications.
 */
import { useEffect, useState } from 'react';
import { Download, Share } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { APP_NAME } from '@/branding';
import { useInstallPrompt, type InstallAvailability } from './useInstallPrompt.ts';

/** How to install by hand, where the browser gives us no way to ask on the user's behalf. */
const INSTRUCTIONS: Partial<Record<InstallAvailability, string[]>> = {
  'ios-instructions': ['Tap the Share button in the browser toolbar.', 'Choose "Add to Home Screen".'],
  'macos-instructions': ['Open the File menu in Safari.', 'Choose "Add to Dock".'],
  // Chromium, but the native dialog has not become available. Its own affordance still works.
  promptable: [
    'Click the install icon at the right-hand end of the address bar.',
    'Or open the browser menu and choose "Install".',
  ],
};

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function InstallDialog({ open, onOpenChange }: Props) {
  const { availability, canPromptNow, beginInstall, abandonInstall, promptInstall } = useInstallPrompt();
  const [working, setWorking] = useState(false);

  // Acquire on open, release on close. The worker only exists for someone who opened this.
  useEffect(() => {
    if (open) {
      beginInstall();
      return undefined;
    }
    return () => undefined;
  }, [open, beginInstall]);

  const close = (next: boolean) => {
    if (!next) abandonInstall();
    onOpenChange(next);
  };

  const install = async () => {
    setWorking(true);
    const outcome = await promptInstall();
    setWorking(false);
    // Accepted closes on its own via `appinstalled`; a dismissal leaves them here, where
    // the instructions still apply.
    if (outcome === 'accepted') onOpenChange(false);
  };

  const steps = INSTRUCTIONS[availability];

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Install {APP_NAME}</DialogTitle>
          <DialogDescription>
            Launch it like an app, get notifications while it&rsquo;s closed, and stay signed in on this browser
            for longer.
          </DialogDescription>
        </DialogHeader>

        {availability === 'installed' ? (
          <p className="text-sm text-muted-foreground">{APP_NAME} is already installed on this device.</p>
        ) : canPromptNow ? (
          // The event arrived — swap the instructions for the real thing, live, while the
          // dialog is open.
          <p className="text-sm text-muted-foreground">
            Your browser can install this for you. It takes a second and nothing is downloaded twice.
          </p>
        ) : steps ? (
          <ol className="text-sm text-muted-foreground space-y-2 list-decimal pl-5">
            {steps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
        ) : (
          <p className="text-sm text-muted-foreground">This browser can&rsquo;t install web apps.</p>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => { close(false); }}>
            {canPromptNow ? 'Not now' : 'Close'}
          </Button>
          {canPromptNow && (
            <Button onClick={() => void install()} disabled={working}>
              <Download className="size-4" aria-hidden />
              Install
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * A one-time nudge above the app shell, because nobody goes looking for an install option
 * they don't know exists.
 *
 * Deliberately passive: it opens the dialog and nothing else. No worker is registered, and
 * no permission is requested, until someone acts on it.
 */
export function InstallBanner() {
  const { availability } = useInstallPrompt();
  const [dismissed, setDismissed] = useState(readDismissed);
  const [open, setOpen] = useState(false);

  if (dismissed || availability === 'installed' || availability === 'unavailable') return null;

  const dismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISSED_KEY, '1');
    } catch {
      // Private mode can throw on write. Not persisting is better than breaking the page.
    }
  };

  return (
    <>
      <div className="border-b border-border bg-bg-subtle px-4 py-2.5" role="region" aria-label={`Install ${APP_NAME}`}>
        <div className="mx-auto flex max-w-5xl items-center gap-3">
          <Share className="hidden size-4 shrink-0 text-muted-foreground sm:block" aria-hidden />
          <p className="min-w-0 flex-1 text-sm">
            Install {APP_NAME} for a faster launch and notifications when the app is closed.
          </p>
          <Button size="sm" onClick={() => { setOpen(true); }}>
            Install
          </Button>
          <Button size="sm" variant="ghost" onClick={dismiss}>
            Dismiss
          </Button>
        </div>
      </div>
      <InstallDialog open={open} onOpenChange={setOpen} />
    </>
  );
}

const DISMISSED_KEY = 'pwa-install-banner-dismissed';

function readDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
}
